import { Injectable } from '@angular/core'
import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpParams
} from '@angular/common/http'
import { SafeUrl } from '@angular/platform-browser'
import { firstValueFrom } from 'rxjs'

import { environment } from 'environments/environment'
import { AuthService } from './auth.service'

export interface AkerMessage {
  id: string
  role: 'assistant' | 'user' | 'system'
  content: string
  createdAt: Date
  usage?: AkerMessageUsage
  backendMessageId?: number
  isStreaming?: boolean
  isError?: boolean
  waitStartedAt?: number
  elapsedSeconds?: number
  thoughtSeconds?: number
  sessionIdHint?: string
  attachments?: AkerAttachment[]
}

export interface AkerAttachment {
  id: string
  name: string
  mimeType: string
  url: SafeUrl
  objectUrl: string
  size?: number
}

export interface AkerStreamRequest {
  message: string
  sessionId: string
  conversation: Pick<AkerMessage, 'role' | 'content'>[]
}

export interface AkerStreamCallbacks {
  onOpen?: () => void
  onChunk?: (chunk: string) => void
  onFile?: (file: AkerStreamFile) => void
  onSession?: (sessionId: string) => void
  onDone?: (payload?: AkerStreamDonePayload) => void
  onError?: (error: AkerStreamError) => void
}

interface AkerStreamEvent {
  eventName: 'chunk' | 'done' | 'error' | 'session' | 'file'
  text?: string
  message?: string
  code?: string
  sessionId?: string
  file?: AkerStreamFile
  usage?: AkerMessageUsage
}

export interface AkerMessageUsage {
  totalConsumption?: number
  inputTokens?: number
  outputTokens?: number
  totalTokens?: number
  totalCost?: number
}

export interface AkerStreamDonePayload {
  sessionId?: string
  usage?: AkerMessageUsage
}

export interface AkerStreamFile {
  name: string
  mimeType: string
  data: string
  size?: number
}

export interface AkerStreamError extends Error {
  code?: string
}

export interface AkerChatRecord {
  id: number
  external_chat_id: string
  title: string
  status: string
  last_message_at?: string
  prompt_tokens?: number
  completion_tokens?: number
  total_tokens?: number
  created_at?: string
  updated_at?: string
}

export interface AkerChatMessageRecord {
  id: number
  chat_id: number
  external_message_id?: string
  message_type: 'sent' | 'received' | 'system'
  sender_name?: string
  message: string
  metadata?: string
  sent_at?: string
  total_consumption?: number
  input_tokens?: number
  output_tokens?: number
  prompt_tokens?: number
  completion_tokens?: number
  total_tokens?: number
  total_cost?: number
  created_at?: string
  updated_at?: string
}

interface AkerChatListResponse {
  error: number
  data?: Array<{
    chat?: AkerChatRecord[]
  }>
}

interface AkerChatResponse {
  error: number
  data?: Array<{
    chat?: AkerChatRecord[]
    message?: AkerChatMessageRecord[]
  }>
  chat?: AkerChatRecord
}

@Injectable({
  providedIn: 'root'
})
export class AkerService {
  private readonly chatApiUrl = environment.akerChatApiUrl || environment.apiUrl
  private readonly chatChannelId = environment.akerChatChannelId || 1
  private readonly streamApiUrl = (
    environment.akerOpencodeApiUrl ||
    environment.bedrockAgentApiUrl ||
    ''
  ).replace(/\/+$/, '')
  private readonly streamRequestTimeoutMs =
    environment.akerOpencodeRequestTimeoutMs || 300000
  private readonly httpChat: HttpClient
  private streamTimedOut = false

  constructor(handler: HttpBackend, private authService: AuthService) {
    this.httpChat = new HttpClient(handler)
  }

  async getChats() {
    const headers = await this.getChatHeaders()
    const params = this.createChatParams()
      .set('limit', '0')
      .set('status', 'active')
      .set('order', 'last_message_at')
      .set('order_way', 'desc')

    const response = await firstValueFrom(
      this.httpChat.get<AkerChatListResponse>(`${this.chatApiUrl}/chat`, {
        headers,
        params
      })
    )

    return response.data?.[0]?.chat || []
  }

  async createChat(externalChatId: string, title: string) {
    const headers = await this.getChatHeaders()
    const response = await firstValueFrom(
      this.httpChat.post<AkerChatResponse>(
        `${this.chatApiUrl}/chat`,
        {
          channel_id: this.chatChannelId,
          external_chat_id: externalChatId,
          title,
          status: 'active'
        },
        {
          headers
        }
      )
    )

    return response.data?.[0]?.chat?.[0]
  }

  async getChatMessages(chatId: number) {
    const headers = await this.getChatHeaders()
    const params = this.createChatParams()
      .set('chat_id', String(chatId))
      .set('limit', '0')
      .set('order', 'sent_at')
      .set('order_way', 'asc')

    const response = await firstValueFrom(
      this.httpChat.get<AkerChatResponse>(`${this.chatApiUrl}/chat/message`, {
        headers,
        params
      })
    )

    return response.data?.[0]?.message || []
  }

  async createChatMessage(
    chatId: number,
    message: AkerMessage,
    senderName: string,
    metadata?: Record<string, unknown>
  ) {
    const headers = await this.getChatHeaders()
    const usagePayload = this.createUsagePayload(message.usage)
    const response = await firstValueFrom(
      this.httpChat.post<AkerChatResponse>(
        `${this.chatApiUrl}/chat/message`,
        {
          channel_id: this.chatChannelId,
          chat_id: chatId,
          external_message_id: message.id,
          message_type: this.resolveMessageType(message.role),
          sender_name: senderName,
          message: message.content,
          metadata: metadata ? JSON.stringify(metadata) : null,
          sent_at: message.createdAt.toISOString(),
          ...usagePayload
        },
        {
          headers
        }
      )
    )

    return response.data?.[0]?.message?.[0]
  }

  async deleteChat(chatId?: number, externalChatId?: string) {
    const headers = await this.getChatHeaders()
    const body: {
      channel_id: number
      chat_id?: number
      external_chat_id?: string
    } = {
      channel_id: this.chatChannelId
    }

    if (chatId) {
      body.chat_id = chatId
    }

    if (externalChatId) {
      body.external_chat_id = externalChatId
    }

    return firstValueFrom(
      this.httpChat.post<AkerChatResponse>(
        `${this.chatApiUrl}/chat/delete`,
        body,
        {
          headers
        }
      )
    )
  }

  streamMessage(request: AkerStreamRequest, callbacks: AkerStreamCallbacks) {
    const controller = new AbortController()
    this.streamTimedOut = false

    this.consumeStream(request, callbacks, controller).catch(
      (error: AkerStreamError) => {
        if (controller.signal.aborted && this.streamTimedOut) {
          const timeoutError = new Error(
            'AKER ha superado el tiempo de espera. Prueba a pedir menos datos o dividir la consulta.'
          ) as AkerStreamError
          timeoutError.code = 'timeout'
          callbacks.onError?.(timeoutError)
          return
        }

        if (controller.signal.aborted) {
          return
        }

        callbacks.onError?.(error)
      }
    )

    return controller
  }

  async cancelSession(sessionId: string) {
    if (!this.streamApiUrl || !sessionId) {
      return
    }

    const response = await fetch(`${this.streamApiUrl}/api/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sessionId })
    })

    if (!response.ok) {
      throw new Error('No se pudo cancelar la ejecucion en AKER.')
    }
  }

  cancelSessionOnUnload(sessionId: string) {
    if (!this.streamApiUrl || !sessionId) {
      return
    }

    fetch(`${this.streamApiUrl}/api/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8'
      },
      body: JSON.stringify({ sessionId }),
      keepalive: true
    }).catch(() => undefined)
  }

  private async consumeStream(
    request: AkerStreamRequest,
    callbacks: AkerStreamCallbacks,
    controller: AbortController
  ) {
    if (!this.streamApiUrl) {
      await this.runMockStream(request.message, callbacks, controller.signal)

      return
    }

    await this.authService.ensureValidAccessToken()

    const timeoutId = setTimeout(() => {
      this.streamTimedOut = true
      controller.abort()
    }, this.streamRequestTimeoutMs)

    const response = await fetch(`${this.streamApiUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message: request.message,
        sessionId: request.sessionId || ''
      }),
      signal: controller.signal
    }).finally(() => {
      clearTimeout(timeoutId)
    })

    if (!response.ok) {
      let message = 'No se pudo conectar con AKER'

      try {
        const payload = await response.json()
        if (payload?.error) {
          message = payload.error
        }
      } catch (_error) {
        // Mantenemos el mensaje por defecto si la respuesta no es JSON.
      }

      throw new Error(message)
    }

    if (!response.body) {
      throw new Error('La respuesta de streaming no contiene body')
    }

    callbacks.onOpen?.()

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let doneEmitted = false

    let isStreamComplete = false

    while (!isStreamComplete) {
      const { done, value } = await reader.read()
      isStreamComplete = done

      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() || ''
      blocks.forEach((block) => {
        const event = this.parseSseBlock(block)
        if (!event) {
          return
        }

        if (event.eventName === 'session' && event.sessionId) {
          callbacks.onSession?.(event.sessionId)
        }

        if (event.eventName === 'chunk' && event.text) {
          callbacks.onChunk?.(event.text)
        }

        if (event.eventName === 'file' && event.file) {
          callbacks.onFile?.(event.file)
        }

        if (event.eventName === 'error') {
          throw this.createStreamError(event)
        }

        if (event.eventName === 'done') {
          doneEmitted = true
          callbacks.onDone?.({
            sessionId: event.sessionId,
            usage: event.usage
          })
        }
      })
    }

    if (buffer.trim()) {
      const event = this.parseSseBlock(buffer.trim())
      if (event?.eventName === 'done') {
        doneEmitted = true
        callbacks.onDone?.({
          sessionId: event.sessionId,
          usage: event.usage
        })
      }
    }

    if (!doneEmitted && !controller.signal.aborted) {
      callbacks.onDone?.({
        sessionId: request.sessionId
      })
    }
  }

  private async runMockStream(
    prompt: string,
    callbacks: AkerStreamCallbacks,
    signal: AbortSignal
  ) {
    callbacks.onOpen?.()

    const message =
      'AKER aun no esta activado en este entorno. El frontend ya esta preparado y este mensaje sale del modo mock. Ultimo prompt: ' +
      prompt

    const chunks = message.match(/.{1,22}/g) || []

    for (const chunk of chunks) {
      if (signal.aborted) {
        return
      }

      callbacks.onChunk?.(chunk)
      await this.delay(60)
    }

    callbacks.onDone?.()
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private createStreamError(event: AkerStreamEvent): AkerStreamError {
    const error = new Error(
      event.message || 'Error desconocido en el stream'
    ) as AkerStreamError

    error.code = event.code

    return error
  }

  private parseSseBlock(block: string) {
    const lines = block
      .split('\n')
      .map((line) => line.trimEnd())
      .filter(Boolean)

    let eventName: AkerStreamEvent['eventName'] = 'chunk'
    let dataText = ''

    lines.forEach((line) => {
      if (line.startsWith(':')) {
        return
      }

      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() as AkerStreamEvent['eventName']
        return
      }

      if (line.startsWith('data:')) {
        dataText += line.slice(5).trim()
      }
    })

    if (!dataText) {
      return null
    }

    try {
      const payload = JSON.parse(dataText)

      return {
        eventName,
        text: payload?.text,
        message: payload?.message,
        code: payload?.code,
        sessionId: payload?.sessionId,
        file: payload?.file,
        usage: this.extractUsagePayload(payload)
      } as AkerStreamEvent
    } catch (_error) {
      return null
    }
  }

  private createUsagePayload(usage?: AkerMessageUsage) {
    return {
      total_consumption: usage?.totalConsumption ?? null,
      input_tokens: usage?.inputTokens ?? null,
      output_tokens: usage?.outputTokens ?? null,
      prompt_tokens: usage?.inputTokens ?? null,
      completion_tokens: usage?.outputTokens ?? null,
      total_tokens: usage?.totalTokens ?? null,
      total_cost: usage?.totalCost ?? null
    }
  }

  private extractUsagePayload(payload: Record<string, unknown>) {
    const usageSource =
      payload?.usage || payload?.metrics || payload?.consumption
    const usage = this.asRecord(usageSource)
    const stepFinish = this.asRecord(payload?.stepFinish)
    const stepFinishInfo = this.asRecord(stepFinish?.info)
    const stepFinishTokens = this.asRecord(stepFinishInfo?.tokens)
    const payloadTokens = this.asRecord(payload?.tokens)

    const totalConsumption = this.parseNumericValue(
      usage?.totalConsumption ??
        usage?.total_consumption ??
        stepFinishInfo?.cost ??
        payload?.cost ??
        payload?.totalConsumption ??
        payload?.total_consumption
    )
    const inputTokens = this.parseNumericValue(
      usage?.inputTokens ??
        usage?.input_tokens ??
        usage?.prompt_tokens ??
        stepFinishTokens?.input ??
        payloadTokens?.input ??
        payload?.inputTokens ??
        payload?.input_tokens ??
        payload?.prompt_tokens
    )
    const outputTokens = this.parseNumericValue(
      usage?.outputTokens ??
        usage?.output_tokens ??
        usage?.completion_tokens ??
        stepFinishTokens?.output ??
        payloadTokens?.output ??
        payload?.outputTokens ??
        payload?.output_tokens ??
        payload?.completion_tokens
    )
    const totalTokens = this.parseNumericValue(
      usage?.totalTokens ??
        usage?.total_tokens ??
        stepFinishTokens?.total ??
        payloadTokens?.total ??
        payload?.totalTokens ??
        payload?.total_tokens
    )
    const totalCost = this.parseNumericValue(
      usage?.totalCost ??
        usage?.total_cost ??
        usage?.cost ??
        stepFinishInfo?.cost ??
        payload?.cost ??
        payload?.totalCost ??
        payload?.total_cost ??
        payload?.cost
    )

    if (
      totalConsumption === undefined &&
      inputTokens === undefined &&
      outputTokens === undefined &&
      totalTokens === undefined &&
      totalCost === undefined
    ) {
      return undefined
    }

    return {
      totalConsumption,
      inputTokens,
      outputTokens,
      totalTokens,
      totalCost
    }
  }

  private parseNumericValue(value: unknown) {
    if (value === undefined || value === null || value === '') {
      return undefined
    }

    const numericValue = Number(value)

    return Number.isFinite(numericValue) ? numericValue : undefined
  }

  private asRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>
    }

    return {}
  }

  private resolveMessageType(
    role: AkerMessage['role']
  ): AkerChatMessageRecord['message_type'] {
    if (role === 'user') {
      return 'sent'
    }

    if (role === 'assistant') {
      return 'received'
    }

    return 'system'
  }

  private createChatParams() {
    return new HttpParams().set('channel_id', String(this.chatChannelId))
  }

  private async getChatHeaders() {
    const token = await this.authService.ensureValidAccessToken()

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      ChannelToken: `Bearer ${environment.akerChatChannelToken || ''}`
    })
  }
}
